/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { CustomerApiService } from './customer-api.service';
import { UserStateService } from '../user-state.service';

const ENDPOINTS = {
  groupDelete: '/v1/search_query/group/delete',
  groupRename: '/v1/search_query/group/rename',
  groupNew: '/v1/search_query/group/new',
  groupView: '/v1/search_query/group/view',
  groupResolve: '/v1/search_query/group/resolve',
  get: '/v1/search_query/get',
  save: '/v1/search_query/save',
  saveArray: '/v1/search_query/save_array',
  delete: '/v1/search_query',
  assignToGroup: '/v1/search_query/assign_to_group',
  removeFromGroup: '/v1/search_query/remove_from_group',
  resolve: '/v1/search_query/resolve',
} as const;

@Injectable({
  providedIn: 'root',
})
export class QueryApiService {
  cachedQueries: any;
  cachedCollections: any;
  queriesChange = true;
  collectionsChange = true;

  constructor(
    private api: CustomerApiService,
    private userState: UserStateService,
  ) {}

  /**
   * Reset both query and collection caches. Mutating methods call this
   * internally; AuthService.authLogout calls it on session end.
   */
  invalidateCache(): void {
    this.queriesChange = true;
    this.collectionsChange = true;
  }

  // GROUPS -------------------------------------------------------------------

  deleteGroupQuery(): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupDelete, {
      uid: this.userState.query.uid,
    });
  }

  editGroupQuery(name: string): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupRename, {
      uid: this.userState.query.uid,
      new_name: name,
    });
  }

  addGroupQuery(name: string): Observable<any> {
    this.invalidateCache();
    return this.api.get(ENDPOINTS.groupNew, { name });
  }

  getGroupsQuery(): Observable<any> {
    if (!this.collectionsChange && this.cachedCollections) {
      return of(this.cachedCollections);
    }
    return this.api.get<any>(ENDPOINTS.groupView).pipe(
      map((collections) => {
        this.collectionsChange = false;
        this.cachedCollections = JSON.parse(JSON.stringify(collections));
        return collections;
      }),
      shareReplay(),
    );
  }

  // QUERIES ------------------------------------------------------------------

  getQuery(): Observable<any> {
    if (!this.queriesChange && this.cachedQueries) {
      return of(this.cachedQueries);
    }
    this.queriesChange = false;
    return this.api.get<any>(ENDPOINTS.get).pipe(
      map((queries) => {
        this.cachedQueries = JSON.parse(JSON.stringify(queries));
        return queries;
      }),
      shareReplay(),
    );
  }

  getQueryGroup(id: string): Observable<any> {
    return this.api.get(ENDPOINTS.get, { group: id });
  }

  addQuery(data: any): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.save, data);
  }

  addQueries(data: any): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.saveArray, data);
  }

  delQueries(idsArray: string[]): Observable<any> {
    this.invalidateCache();
    return this.api.deleteWithBody(ENDPOINTS.delete, { uids: idsArray });
  }

  // GROUP ASSIGNMENT ---------------------------------------------------------

  addQueriesToGroup(queriesIds: string[], groupId: string): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.assignToGroup, {
      group: groupId,
      query_ids: queriesIds,
    });
  }

  removeQueriesFromGroup(
    queriesIds: string[],
    groupId: string,
  ): Observable<any> {
    this.invalidateCache();
    return this.api.post(ENDPOINTS.removeFromGroup, {
      group: groupId,
      query_ids: queriesIds,
    });
  }

  // RESOLUTION ---------------------------------------------------------------

  resolveQuery(uid: string): Observable<any> {
    return this.api.get(ENDPOINTS.resolve, { uid });
  }

  resolveQueryGroup(uid: string): Observable<any> {
    return this.api.get(ENDPOINTS.groupResolve, { uid });
  }
}
